/**
 * 前端组队管理器
 * 管理组队UI和状态
 */
import DebugHelper from './DebugHelper.js';

export default class TeamManager {
    constructor(scene, networkManager) {
        this.scene = scene;
        this.networkManager = networkManager;
        
        // 组队状态
        this.teamId = null;
        this.isLeader = false;
        this.teamMembers = new Map(); // { playerId: { playerName, ... } }
        this.pendingInvites = new Map(); // { inviteId: { teamId, fromPlayerId, fromPlayerName } }
        
        // UI元素
        this.teamUI = null;
        this.inviteDialog = null;
    }
    
    /**
     * 初始化组队管理器
     */
    init() {
        if (!this.networkManager) {
            DebugHelper.warn('TeamManager: 网络管理器不存在');
            return;
        }

        this.destroyNetworkSubscriptions();
        
        this._networkUnsubs = [
            this.networkManager.on('teamCreated', (teamId, teamInfo) => {
                this.handleTeamCreated(teamId, teamInfo);
            }),
            this.networkManager.on('teamInvite', (payload) => {
                this.handleTeamInvite(payload);
            }),
            this.networkManager.on('teamMemberJoined', (payload) => {
                this.handleTeamMemberJoined(payload);
            }),
            this.networkManager.on('teamMemberLeft', (payload) => {
                this.handleTeamMemberLeft(payload);
            }),
            this.networkManager.on('teamLeft', (payload) => {
                this.handleTeamLeft(payload);
            }),
            this.networkManager.on('teamDisbanded', (payload) => {
                this.handleTeamDisbanded(payload);
            })
        ];
        
        this.createTeamUI();
    }

    destroyNetworkSubscriptions() {
        if (this._networkUnsubs) {
            this._networkUnsubs.forEach((unsub) => {
                if (typeof unsub === 'function') {
                    unsub();
                }
            });
            this._networkUnsubs = null;
        }
    }
    
    /**
     * 创建组队UI
     */
    createTeamUI() {
        // 组队面板（右上角）
        const panelX = this.scene.cameras.main.width - 200;
        const panelY = 20;
        
        // 背景
        const bg = this.scene.add.graphics();
        bg.fillStyle(0x1a1a2e, 0.8);
        bg.fillRoundedRect(panelX - 10, panelY - 10, 190, 150, 5);
        bg.lineStyle(2, 0x4cc9f0, 1);
        bg.strokeRoundedRect(panelX - 10, panelY - 10, 190, 150, 5);
        bg.setDepth(100);
        bg.setScrollFactor(0);
        bg.setVisible(false);
        
        // 标题
        const title = this.scene.add.text(
            panelX,
            panelY,
            '队伍',
            {
                fontSize: '16px',
                fill: '#4cc9f0',
                fontFamily: 'monospace',
                fontWeight: 'bold'
            }
        );
        title.setOrigin(0, 0.5);
        title.setDepth(101);
        title.setScrollFactor(0);
        title.setVisible(false);
        
        // 成员列表
        const membersText = this.scene.add.text(
            panelX,
            panelY + 30,
            '暂无队伍',
            {
                fontSize: '12px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                wordWrap: { width: 170 }
            }
        );
        membersText.setOrigin(0, 0);
        membersText.setDepth(101);
        membersText.setScrollFactor(0);
        membersText.setVisible(false);
        
        // 操作按钮容器
        const buttonY = panelY + 100;
        
        // 创建队伍按钮
        const createButton = this.scene.add.text(
            panelX,
            buttonY,
            '创建队伍',
            {
                fontSize: '12px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: '#00aa00',
                padding: { x: 10, y: 5 }
            }
        );
        createButton.setOrigin(0, 0.5);
        createButton.setDepth(101);
        createButton.setScrollFactor(0);
        createButton.setInteractive({ useHandCursor: true });
        createButton.setVisible(false);
        
        createButton.on('pointerdown', () => {
            this.createTeam();
        });
        
        // 离开队伍按钮
        const leaveButton = this.scene.add.text(
            panelX + 100,
            buttonY,
            '离开',
            {
                fontSize: '12px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: '#aa0000',
                padding: { x: 10, y: 5 }
            }
        );
        leaveButton.setOrigin(0, 0.5);
        leaveButton.setDepth(101);
        leaveButton.setScrollFactor(0);
        leaveButton.setInteractive({ useHandCursor: true });
        leaveButton.setVisible(false);
        
        leaveButton.on('pointerdown', () => {
            this.leaveTeam();
        });
        
        this.teamUI = {
            bg: bg,
            title: title,
            membersText: membersText,
            createButton: createButton,
            leaveButton: leaveButton
        };
    }
    
    /**
     * 更新队伍UI
     */
    updateTeamUI() {
        if (!this.teamUI) return;
        
        if (this.teamId) {
            // 有队伍，显示成员列表
            this.teamUI.bg.setVisible(true);
            this.teamUI.title.setVisible(true);
            this.teamUI.membersText.setVisible(true);
            this.teamUI.createButton.setVisible(false);
            this.teamUI.leaveButton.setVisible(true);
            
            let membersList = '';
            this.teamMembers.forEach((member, playerId) => {
                const prefix = this.isLeader && playerId === this.networkManager.playerId ? '👑 ' : '';
                membersList += `${prefix}${member.playerName || '玩家'}\n`;
            });
            
            this.teamUI.membersText.setText(membersList || '暂无成员');
        } else {
            // 无队伍，显示创建按钮
            this.teamUI.bg.setVisible(true);
            this.teamUI.title.setVisible(true);
            this.teamUI.membersText.setVisible(false);
            this.teamUI.createButton.setVisible(true);
            this.teamUI.leaveButton.setVisible(false);
        }
    }
    
    /**
     * 设置菜单上下文（用于在菜单中创建队伍后关闭菜单）
     * @param {Phaser.Scene} menuScene - 菜单场景
     */
    setMenuContext(menuScene) {
        this.menuScene = menuScene;
    }
    
    /**
     * 创建队伍
     */
    createTeam() {
        if (this.networkManager && this.networkManager.isConnected()) {
            this.networkManager.createTeam();
        }
    }
    
    /**
     * 离开队伍
     */
    leaveTeam() {
        if (this.networkManager && this.networkManager.isConnected()) {
            this.networkManager.leaveTeam();
        }
    }
    
    /**
     * 邀请玩家
     * @param {string} playerId - 玩家ID
     */
    invitePlayer(playerId) {
        if (this.networkManager && this.networkManager.isConnected()) {
            this.networkManager.invitePlayerToTeam(playerId);
        }
    }
    
    /**
     * 处理队伍创建
     */
    handleTeamCreated(teamId, teamInfo) {
        DebugHelper.log('收到队伍创建消息:', { teamId, teamInfo });
        
        this.teamId = teamId;
        this.isLeader = teamInfo.leaderId === this.networkManager.playerId;
        this.teamMembers.clear();
        
        // 添加所有成员
        if (teamInfo.members && Array.isArray(teamInfo.members)) {
            teamInfo.members.forEach(playerId => {
                const player = this.networkManager.getPlayer(playerId);
                if (player) {
                    this.teamMembers.set(playerId, {
                        playerId: playerId,
                        playerName: player.playerName || '玩家'
                    });
                } else {
                    // 如果找不到玩家，至少保存ID
                    this.teamMembers.set(playerId, {
                        playerId: playerId,
                        playerName: '玩家'
                    });
                }
            });
        }
        
        // 确保队长也在列表中（即使 members 数组中没有）
        const leaderId = teamInfo.leaderId;
        if (leaderId && !this.teamMembers.has(leaderId)) {
            const leaderPlayer = this.networkManager.getPlayer(leaderId);
            this.teamMembers.set(leaderId, {
                playerId: leaderId,
                playerName: leaderPlayer ? (leaderPlayer.playerName || '玩家') : (leaderId === this.networkManager.playerId ? (this.networkManager.playerName || '玩家') : '玩家')
            });
        }
        
        // 如果没有成员信息，至少添加自己
        if (this.teamMembers.size === 0 && this.networkManager.playerId) {
            this.teamMembers.set(this.networkManager.playerId, {
                playerId: this.networkManager.playerId,
                playerName: this.networkManager.playerName || '玩家'
            });
        }
        
        DebugHelper.log('handleTeamCreated - 队伍成员列表:', {
            teamMembersSize: this.teamMembers.size,
            members: Array.from(this.teamMembers.entries()).map(([id, m]) => ({
                playerId: id,
                playerName: m.playerName
            })),
            teamInfoMembers: teamInfo.members,
            leaderId: leaderId
        });
        
        // 更新多人玩家管理器的队长信息
        DebugHelper.log('准备更新多人玩家管理器', {
            hasManager: !!this.scene.multiplayerPlayerManager,
            isLeader: this.isLeader,
            leaderId: teamInfo.leaderId,
            members: teamInfo.members,
            localPlayerId: this.networkManager.playerId
        });
        
        if (this.scene.multiplayerPlayerManager) {
            const memberIds = teamInfo.members || [];
            DebugHelper.log('调用setLocalPlayerAsLeader或setTeamLeader', {
                isLeader: this.isLeader,
                memberIds: memberIds
            });
            
            if (this.isLeader) {
                // 本地玩家是队长，设置队员跟随
                DebugHelper.log('本地玩家是队长，调用setLocalPlayerAsLeader');
                this.scene.multiplayerPlayerManager.setLocalPlayerAsLeader(memberIds);
            } else {
                // 本地玩家是队员，需要跟随队长
                DebugHelper.log('本地玩家是队员，调用setTeamLeader', { leaderId: teamInfo.leaderId });
                this.scene.multiplayerPlayerManager.setTeamLeader(teamInfo.leaderId, memberIds);
            }
        } else {
            DebugHelper.error('multiplayerPlayerManager不存在！');
        }
        
        this.updateTeamUI();
        DebugHelper.log('队伍创建成功:', teamId, '成员数:', this.teamMembers.size, '是队长:', this.isLeader);
        
        // 如果是在菜单中创建的队伍，关闭菜单
        if (this.menuScene) {
            DebugHelper.log('TeamManager: 检测到菜单上下文，准备关闭菜单');
            // 保存菜单场景引用，因为下面会清除
            const menuSceneRef = this.menuScene;
            this.menuScene = null; // 先清除菜单上下文，避免重复处理
            
            // 延迟关闭菜单，确保状态更新完成
            menuSceneRef.time.delayedCall(200, () => {
                if (menuSceneRef && typeof menuSceneRef.closeMenu === 'function') {
                    DebugHelper.log('TeamManager: 关闭菜单');
                    menuSceneRef.closeMenu();
                } else {
                    DebugHelper.warn('TeamManager: 菜单场景不存在或没有 closeMenu 方法');
                }
            });
        } else {
            // 不在菜单中，显示成功消息
            if (this.scene.worldUI) {
                this.scene.worldUI.showMessage('队伍创建成功！');
            }
        }
    }
    
    /**
     * 处理组队邀请
     */
    handleTeamInvite(payload) {
        DebugHelper.log('handleTeamInvite 被调用', payload);
        const { inviteId, teamId, fromPlayerId, fromPlayerName } = payload;
        
        this.pendingInvites.set(inviteId, {
            inviteId: inviteId,
            teamId: teamId,
            fromPlayerId: fromPlayerId,
            fromPlayerName: fromPlayerName
        });
        
        // 显示邀请对话框
        this.showInviteDialog(payload);
    }
    
    /**
     * 显示邀请对话框
     */
    showInviteDialog(payload) {
        const { inviteId, fromPlayerName } = payload;
        const centerX = this.scene.cameras.main.centerX;
        const centerY = this.scene.cameras.main.centerY;
        
        // 背景遮罩
        const overlay = this.scene.add.graphics();
        overlay.fillStyle(0x000000, 0.7);
        overlay.fillRect(0, 0, this.scene.cameras.main.width, this.scene.cameras.main.height);
        overlay.setDepth(200);
        overlay.setScrollFactor(0);
        
        // 对话框背景
        const dialogBg = this.scene.add.graphics();
        dialogBg.fillStyle(0x1a1a2e, 0.95);
        dialogBg.fillRoundedRect(centerX - 200, centerY - 80, 400, 160, 10);
        dialogBg.lineStyle(3, 0x4cc9f0, 1);
        dialogBg.strokeRoundedRect(centerX - 200, centerY - 80, 400, 160, 10);
        dialogBg.setDepth(201);
        dialogBg.setScrollFactor(0);
        
        // 消息文本
        const messageText = this.scene.add.text(
            centerX,
            centerY - 30,
            `${fromPlayerName} 邀请你加入队伍`,
            {
                fontSize: '18px',
                fill: '#ffffff',
                fontFamily: 'monospace'
            }
        );
        messageText.setOrigin(0.5);
        messageText.setDepth(202);
        messageText.setScrollFactor(0);
        
        // 接受按钮
        const acceptButton = this.scene.add.text(
            centerX - 80,
            centerY + 30,
            '接受',
            {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: '#00aa00',
                padding: { x: 20, y: 8 }
            }
        );
        acceptButton.setOrigin(0.5);
        acceptButton.setDepth(202);
        acceptButton.setScrollFactor(0);
        acceptButton.setInteractive({ useHandCursor: true });
        
        // 拒绝按钮
        const rejectButton = this.scene.add.text(
            centerX + 80,
            centerY + 30,
            '拒绝',
            {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: '#aa0000',
                padding: { x: 20, y: 8 }
            }
        );
        rejectButton.setOrigin(0.5);
        rejectButton.setDepth(202);
        rejectButton.setScrollFactor(0);
        rejectButton.setInteractive({ useHandCursor: true });
        
        const closeDialog = () => {
            overlay.destroy();
            dialogBg.destroy();
            messageText.destroy();
            acceptButton.destroy();
            rejectButton.destroy();
        };
        
        acceptButton.on('pointerdown', () => {
            DebugHelper.log('TeamManager: 队员点击接受邀请按钮', { inviteId: inviteId });
            this.networkManager.acceptTeamInvite(inviteId);
            closeDialog();
        });
        
        rejectButton.on('pointerdown', () => {
            this.networkManager.rejectTeamInvite(inviteId);
            closeDialog();
        });
        
        // 5秒后自动关闭
        this.scene.time.delayedCall(5000, () => {
            if (overlay.active) {
                this.networkManager.rejectTeamInvite(inviteId);
                closeDialog();
            }
        });
    }
    
    /**
     * 处理队员加入
     */
    handleTeamMemberJoined(payload) {
        DebugHelper.log('handleTeamMemberJoined 被调用', payload);
        
        const { playerId, playerName, teamInfo } = payload;
        
        DebugHelper.log('handleTeamMemberJoined: 处理队员加入', {
            playerId: playerId,
            playerName: playerName,
            teamInfo: teamInfo,
            localPlayerId: this.networkManager.playerId
        });
        
        if (teamInfo.members) {
            this.teamMembers.clear();
            teamInfo.members.forEach(memberId => {
                const player = this.networkManager.getPlayer(memberId);
                if (player) {
                    this.teamMembers.set(memberId, {
                        playerId: memberId,
                        playerName: player.playerName || '玩家'
                    });
                } else {
                    // 如果找不到玩家，至少保存ID和名称（可能是自己）
                    this.teamMembers.set(memberId, {
                        playerId: memberId,
                        playerName: memberId === this.networkManager.playerId ? (this.networkManager.playerName || '玩家') : '玩家'
                    });
                }
            });
            
            // 确保队长也在列表中
            const leaderId = teamInfo.leaderId;
            if (leaderId && !this.teamMembers.has(leaderId)) {
                const leaderPlayer = this.networkManager.getPlayer(leaderId);
                this.teamMembers.set(leaderId, {
                    playerId: leaderId,
                    playerName: leaderPlayer ? (leaderPlayer.playerName || '玩家') : (leaderId === this.networkManager.playerId ? (this.networkManager.playerName || '玩家') : '玩家')
                });
            }
        }
        
        DebugHelper.log('handleTeamMemberJoined - 队伍成员列表:', {
            teamMembersSize: this.teamMembers.size,
            members: Array.from(this.teamMembers.entries()).map(([id, m]) => ({
                playerId: id,
                playerName: m.playerName
            }))
        });
        
        this.teamId = teamInfo.id || this.teamId;
        this.isLeader = teamInfo.leaderId === this.networkManager.playerId;
        
        DebugHelper.log('handleTeamMemberJoined: 更新队伍状态', {
            teamId: this.teamId,
            isLeader: this.isLeader,
            leaderId: teamInfo.leaderId,
            members: teamInfo.members,
            localPlayerId: this.networkManager.playerId
        });
        
        // 更新多人玩家管理器的队长信息
        if (this.scene.multiplayerPlayerManager) {
            const memberIds = teamInfo.members || [];
            DebugHelper.log('handleTeamMemberJoined: 准备更新多人玩家管理器', {
                isLeader: this.isLeader,
                memberIds: memberIds,
                leaderId: teamInfo.leaderId
            });
            
            if (this.isLeader) {
                // 本地玩家是队长，设置队员跟随
                DebugHelper.log('handleTeamMemberJoined: 本地玩家是队长，调用setLocalPlayerAsLeader');
                this.scene.multiplayerPlayerManager.setLocalPlayerAsLeader(memberIds);
            } else {
                // 本地玩家是队员，需要跟随队长
                DebugHelper.log('handleTeamMemberJoined: 本地玩家是队员，调用setTeamLeader', { leaderId: teamInfo.leaderId });
                this.scene.multiplayerPlayerManager.setTeamLeader(teamInfo.leaderId, memberIds);
            }
        } else {
            DebugHelper.error('handleTeamMemberJoined: multiplayerPlayerManager不存在！');
        }
        
        this.updateTeamUI();
        DebugHelper.log('handleTeamMemberJoined: 完成', {
            teamMembersCount: this.teamMembers.size,
            isLeader: this.isLeader
        });
    }
    
    /**
     * 处理队员离开
     */
    handleTeamMemberLeft(payload) {
        const { playerId, teamInfo } = payload;
        
        this.teamMembers.delete(playerId);
        
        if (teamInfo) {
            this.isLeader = teamInfo.leaderId === this.networkManager.playerId;
        }
        
        this.updateTeamUI();
    }
    
    /**
     * 处理离开队伍
     */
    handleTeamLeft(payload) {
        this.teamId = null;
        this.isLeader = false;
        this.teamMembers.clear();
        
        // 清除跟随状态
        if (this.scene.multiplayerPlayerManager) {
            this.scene.multiplayerPlayerManager.clearTeam();
        }
        
        this.updateTeamUI();
    }
    
    /**
     * 处理队伍解散
     */
    handleTeamDisbanded(payload) {
        this.teamId = null;
        this.isLeader = false;
        this.teamMembers.clear();
        
        // 清除跟随状态
        if (this.scene.multiplayerPlayerManager) {
            this.scene.multiplayerPlayerManager.clearTeam();
        }
        
        this.updateTeamUI();
    }
    
    /**
     * 检查是否是队长
     */
    isTeamLeader() {
        return this.isLeader;
    }
    
    /**
     * 获取队伍ID
     */
    getTeamId() {
        return this.teamId;
    }
    
    /**
     * 获取队伍成员
     */
    getTeamMembers() {
        return Array.from(this.teamMembers.values());
    }
    
    /**
     * 清理
     */
    destroy() {
        this.destroyNetworkSubscriptions();

        if (this.teamUI) {
            this.teamUI.bg.destroy();
            this.teamUI.title.destroy();
            this.teamUI.membersText.destroy();
            this.teamUI.createButton.destroy();
            this.teamUI.leaveButton.destroy();
        }
    }
}

